// src/pages/CRM/Finance/PaymentList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { hasPermission } from '../../../../utils/permissionUtils';
import axiosInstance from '../../../../utils/axiosInstance';
import { PaymentMethodLabels } from '../Core/crmEnums';
import { useSnackbar } from 'notistack';
import PaymentDialog from './PaymentDialog';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const PaymentList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, payment: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  useEffect(() => {
    document.title = 'Payments | KlonIT CRM';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadPayments();
    }
  }, [canView, chatbotId, page, rowsPerPage]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...(chatbotId && { chatbot_id: chatbotId }),
        ...(search && { search }),
      };
      const response = await axiosInstance.get('/api/crm/payments', { params });
      if (response.data.success) {
        setPayments(response.data.data?.payments || []);
        setTotalCount(response.data.data?.total || 0);
      } else {
        setError(response.data.message || 'Failed to load payments');
      }
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [chatbotId, page, rowsPerPage, search]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      loadPayments();
    }
  };

  const handleDeletePayment = async () => {
    if (!deleteDialog.payment) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.delete(`/api/crm/payments/${deleteDialog.payment.id}`);
      if (response.data.success) {
        enqueueSnackbar('Payment deleted successfully', { variant: 'success' });
        setDeleteDialog({ open: false, payment: null });
        loadPayments();
      } else {
        enqueueSnackbar(response.data.message || 'Failed to delete payment', { variant: 'error' });
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
      enqueueSnackbar('Failed to delete payment', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentSaved = () => {
    setPaymentDialogOpen(false);
    loadPayments();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (!canView && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view payments.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, fontFamily }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/salestransaction`)}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700, fontFamily }}>
            Payments
          </Typography>
        </Stack>
        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setPaymentDialogOpen(true)}
            sx={{ fontFamily, textTransform: 'none', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            Record Payment
          </Button>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search payments..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 400, fontFamily, '& .MuiInputBase-root': { fontFamily } }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={loadPayments}>Retry</Button>
        }>{error}</Alert>
      )}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Invoice #</TableCell>
                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Method</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Reference #</TableCell>
                <TableCell align="center" sx={{ fontFamily, fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, fontFamily, color: '#9ca3af' }}>
                    No payments found
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell sx={{ fontFamily }}>{payment.contact ? `${payment.contact.first_name || ''} ${payment.contact.last_name || ''}`.trim() : '-'}</TableCell>
                    <TableCell sx={{ fontFamily, color: '#6b7280' }}>{payment.invoice_number || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily, fontWeight: 600, color: '#22c55e' }}>
                      {formatCurrency(payment.amount)}
                    </TableCell>
                    <TableCell sx={{ fontFamily, color: '#6b7280' }}>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell>
                      <Chip
                        label={PaymentMethodLabels[payment.payment_method] || payment.payment_method}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily, color: '#6b7280' }}>{payment.reference_number || '-'}</TableCell>
                    <TableCell align="center">
                      {canModify && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteDialog({ open: true, payment })}
                            disabled={actionLoading}
                          >
                            <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, payment: null })}>
        <DialogTitle sx={{ fontFamily }}>Delete Payment</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily }}>
            Are you sure you want to delete this payment of <strong>{formatCurrency(deleteDialog.payment?.amount)}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, payment: null })}
            sx={{ fontFamily, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeletePayment}
            color="error"
            variant="contained"
            disabled={actionLoading}
            sx={{ fontFamily, textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onSaved={handlePaymentSaved}
      />
    </Box>
  );
};

export default PaymentList;
